import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ptzApi, snapshotApi } from '../../services/api';
import { useAppStore } from '../../store/appStore';
import { useDriverFor } from '../../hooks/useCameraDrivers';

// The d-pad's footprint sets the size of both controls: the joystick circle is exactly as
// wide as the pad below it, so resizing the keys moves the circle with them rather than
// leaving the two to drift apart.
//
// The joystick's usable travel is derived, not written down. The knob has to stay inside the
// circle, so the radius is half the difference between them. It was a hard-coded 50 that
// happened to suit the old 130px base; changing the circle without it would mean full speed
// short of the rim, which feels like a broken stick rather than a layout mistake.
const DPAD_CELL = 60;
const DPAD_GAP = 6;
const DPAD_SIZE = DPAD_CELL * 3 + DPAD_GAP * 2;
const JOY_BASE = DPAD_SIZE;
// The handle stays at its original 36px. It does not need to grow with the circle — a
// smaller handle in a bigger base reads more precisely and leaves more travel, since the
// usable radius is what is left after the handle.
const JOY_KNOB = 36;
const JOY_MAX_R = (JOY_BASE - JOY_KNOB) / 2;

export default function PTZController({ camera, onMove }) {
  // How this camera's controls behave — which gestures it needs, how zoom is expressed —
  // comes from its driver. Nothing here branches on the brand.
  const driver = useDriverFor(camera);
  const driverRef = useRef(null);
  driverRef.current = driver;   // callbacks below are memoised with [] and read it live
  const { addToast } = useAppStore();
  const [position, setPosition] = useState({ pan: 0, tilt: 0, zoom: 0 });
  // PTZ speeds live in localStorage (owned by the Settings tab / PTZSettingsPanel). The
  // pad reads them fresh at gesture start via readSpeed(), so slider changes apply live
  // with no shared React state.
  const readSpeed = (k, d) => parseInt(localStorage.getItem(`axisvms_ptz_${k}_speed_${camera.id}`) || d, 10);
  const [tours, setTours] = useState([]);
  const [activeTourId, setActiveTourId] = useState(null);
  const [toursLoading, setToursLoading] = useState(false);
  const [tourActionId, setTourActionId] = useState(null);
  const [capturing, setCapturing] = useState(false);

  const [zoomLimits, setZoomLimits] = useState(null); // { minZoom, maxZoom } from camera
  const zoomLimitsRef = useRef(null); // ref copy so loadPosition can read without stale closure

  const joystickRef = useRef(null);
  const isDragging = useRef(false);
  const pendingStop = useRef(false);
  const lastMoveSent = useRef(0);
  const joystickCenter = useRef({ x: 0, y: 0 });
  const joystickSpeedRef = useRef(readSpeed('joy', '50'));
  const dpadSpeedRef = useRef(readSpeed('dpad', '50'));
  const zoomSpeedRef = useRef(readSpeed('zoom', '50'));
  const lastSentDir = useRef({ pan: 0, tilt: 0 });
  const moveInFlight = useRef(false);
  const desiredDir = useRef({ pan: 0, tilt: 0, speed: 50 });
  const tapRef = useRef(null); // { timer, pan, tilt, speed } while a Dahua press is undecided
  const holdKeepAliveRef = useRef(null); // re-sends the held command so the server watchdog holds off
  // The backend stops a Dahua continuous move if it hears nothing for 4s (see
  // routes/dahua.js HOLD_WATCHDOG_MS), which is what stops a camera when a client dies
  // mid-hold. Re-send the active command at half that interval so a deliberate long hold
  // keeps running while an abandoned one still gets cut.
  const HOLD_KEEPALIVE_MS = 2000;
  // Press shorter than this is a tap, not a hold. Starting point — worth tuning against
  // how operators actually tap; too short and deliberate taps register as holds.
  const TAP_WINDOW_MS = 180;
  // How far a tap recentres, as a fraction of the frame, at full deflection and speed 100.
  // Scaled by the control's speed setting, and for the joystick by how far it was pushed.
  // 0.5 is the geometric ceiling: it recentres on the frame edge, which is the furthest a
  // recentre can move without pointing at something that was never on screen. Default
  // speed 50 therefore steps a quarter-frame.
  // The angular travel self-scales with zoom — a fraction of frame is a fraction of the
  // field of view, which narrows as you zoom in — so no zoom term is needed here.
  const TAP_MAX_FRAC = 0.5;
  const zoomPollRef = useRef(null);
  const zoomSliderTrackRef = useRef(null);
  const zoomPointerDown = useRef(false);
  const positionRef = useRef({ pan: 0, tilt: 0, zoom: 0 });
  const zoomTargetPct = useRef(null);
  const zoomTargetCheckRef = useRef(null);
  // Both cameras: small zoom value = wide angle, large = tele. Self-calibrates from operation.
  const zoomObservedMin = useRef(null); // smallest seen = fully wide (0%)
  const zoomObservedMax = useRef(null); // largest seen = fully tele (100%)
  const zoomLogRef = useRef(false);     // position is proportional to magnification

  useEffect(() => {
    loadPosition();
    loadTours();
    // Fetch zoom limits for both camera types so the slider is accurate immediately on load.
    // If the endpoint fails (camera doesn't support it), falls back to observed range.
    ptzApi.ptzLimits(camera.id)
      .then(r => setZoomLimits(r.data))
      .catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [camera.id]);

  // When limits arrive, fix the range to the camera's actual min/max and stop
  // self-calibrating.  Position polls during preset moves can transiently report
  // values outside the real range (e.g. Dahua passing through max zoom mid-move)
  // which would corrupt the observed range and push all subsequent readings to 100%.
  //
  // Exception: zoomEstimated limits are the camera's magnification range, not its
  // position scale (an uncalibrated Dahua reports e.g. 1-30x caps while position runs
  // ~4-97). Don't seed from those at all — widening only ever expands outward, so a caps
  // floor that sits *below* the real floor (1 vs 4.27) can never correct itself and the
  // bar reads a few percent when fully wide forever. Pure observation converges to the
  // truth once both ends have been visited. Run Calibrate Zoom for an exact range.
  useEffect(() => {
    if (!zoomLimits) return;
    zoomLimitsRef.current = zoomLimits.zoomEstimated ? null : zoomLimits;
    zoomLogRef.current = !!zoomLimits.zoomLogScale;
    if (zoomLimits.zoomEstimated) return;
    zoomObservedMin.current = zoomLimits.minZoom;
    zoomObservedMax.current = zoomLimits.maxZoom;
  }, [zoomLimits]);

  // Poll position every 3s so the slider stays accurate after preset changes,
  // hardware zoom, or any other external position change.
  useEffect(() => {
    const id = setInterval(() => {
      if (!zoomPollRef.current && !zoomPointerDown.current) loadPosition();
    }, 3000);
    return () => clearInterval(id);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [camera.id]);

  // Guard against pile-up: a remote camera can take longer to answer than the poll
  // interval, and overlapping in-flight polls only add load, push responses past the
  // request timeout, and stall the readout for far longer than the interval.
  const posInFlight = useRef(false);

  async function loadPosition() {
    if (posInFlight.current) return;
    posInFlight.current = true;
    try {
      const res = await ptzApi.ptzPosition(camera.id);
      const z = res.data.zoom;
      if (z != null && !isNaN(z)) {
        if (!zoomLimitsRef.current) {
          // No limits yet: full self-calibration both directions
          if (zoomObservedMin.current === null || z < zoomObservedMin.current) zoomObservedMin.current = z;
          if (zoomObservedMax.current === null || z > zoomObservedMax.current) zoomObservedMax.current = z;
        }
        // Once limits are loaded, the range is fixed from the camera's caps.
        // We do not self-calibrate after that to avoid transient values (e.g. Dahua
        // briefly reporting max zoom during a preset move) corrupting the range.
      }
      positionRef.current = res.data;
      setPosition(res.data);
    } catch {}
    finally { posInFlight.current = false; }
  }

  async function loadTours() {
    setToursLoading(true);
    try {
      const res = await ptzApi.getTours(camera.id);
      setTours(res.data);
      const running = res.data.find(t => t.running);
      setActiveTourId(running ? running.id : null);
    } catch { setTours([]); }
    finally { setToursLoading(false); }
  }

  async function toggleTour(tour) {
    setTourActionId(tour.id);
    try {
      const tourApi = ptzApi;
      if (activeTourId === tour.id) {
        await tourApi.stopTour(camera.id, tour.id);
        setActiveTourId(null);
        addToast({ type: 'info', message: `Patrol tour "${tour.name}" stopped` });
      } else {
        if (activeTourId != null) await tourApi.stopTour(camera.id, activeTourId);
        await tourApi.startTour(camera.id, tour.id);
        setActiveTourId(tour.id);
        addToast({ type: 'success', message: `Patrol tour "${tour.name}" started` });
      }
    } catch (err) {
      addToast({ type: 'error', message: err.response?.data?.error || 'Failed to toggle tour' });
    } finally { setTourActionId(null); }
  }

  // Returns true once we have enough data to accurately map zoom position to 0-100%.
  function zoomRangeKnown() {
    const obsMin = zoomObservedMin.current;
    const obsMax = zoomObservedMax.current;
    return obsMin != null && obsMax != null && obsMax > obsMin;
  }

  // Normalize zoom position to 0% (fully wide) → 100% (fully tele).
  // Both cameras: small value = wide angle, large value = tele.
  // Uses observed range (seeded from limits on load, refined by actual operations).
  //
  // Dahua reports position proportional to magnification (calibrated max/min lands exactly
  // on the camera's rated 30x), so mapping it linearly crushes everything you can perceive
  // into the bottom of the bar — 3.1x of 30x reads 7%, yet the field of view is already a
  // third of wide, and the entire top half spans 15x-30x which all looks alike. Map by
  // ratio instead: equal travel = equal FOV ratio, and 50% is the geometric midpoint.
  function isLogZoom() {
    return !!zoomLogRef.current
      && zoomObservedMin.current > 0
      && zoomObservedMax.current > zoomObservedMin.current;
  }

  function getZoomPct(z) {
    if (!zoomRangeKnown()) return null; // unknown — caller handles separately
    const zNum = (z != null && !isNaN(z)) ? z : 0;
    const obsMin = zoomObservedMin.current;
    const obsMax = zoomObservedMax.current;
    if (isLogZoom() && zNum > 0) {
      return Math.min(100, Math.max(0, Math.log(zNum / obsMin) / Math.log(obsMax / obsMin) * 100));
    }
    return Math.min(100, Math.max(0, ((zNum - obsMin) / (obsMax - obsMin)) * 100));
  }

  // Inverse of getZoomPct — bar position back to a camera zoom value.
  function zoomPctToValue(pct) {
    const obsMin = zoomObservedMin.current;
    const obsMax = zoomObservedMax.current;
    if (isLogZoom()) return obsMin * Math.pow(obsMax / obsMin, pct / 100);
    return obsMin + (obsMax - obsMin) * pct / 100;
  }

  // Zoom toward a target % on the slider track.
  // Dahua: uses PositionABS — Dahua doesn't stream real-time position during moves so
  //   polling-based auto-stop never sees the camera moving and always runs to 100%.
  // Axis: continuous zoom + poll auto-stop (reports position in real-time).
  // Unknown range: direction-only (above/below center).
  const lastAbsZoomAt = useRef(0); // throttle PositionABS during drag
  function startZoomToTarget(targetPct) {
    clearInterval(zoomTargetCheckRef.current);
    zoomTargetCheckRef.current = null;

    if (!zoomRangeKnown()) {
      // No range data — use track position relative to center to determine direction.
      const dir = targetPct > 55 ? 1 : targetPct < 45 ? -1 : 0;
      if (dir !== 0) handleZoomPress(dir);
      return;
    }

    // Absolute-track cameras are positioned directly; continuous-track ones are driven with
    // a move and stopped by the polling watcher further down.
    if (driverRef.current?.zoomControl.track === 'absolute') {
      // Throttle during the drag so we don't flood the camera.
      const now = Date.now();
      if (now - lastAbsZoomAt.current < (driverRef.current?.controls.minCommandIntervalMs ?? 300)) return;
      lastAbsZoomAt.current = now;

      const targetZoom = zoomPctToValue(targetPct);
      ptzApi.ptzAbsolute(camera.id, {
        pan: positionRef.current.pan,
        tilt: positionRef.current.tilt,
        zoom: parseFloat(targetZoom.toFixed(3)),
      }).catch(() => {});
      // Hold zoomTargetCheckRef non-null so onZoomTrackPointerUp doesn't send a stop
      // command that would cancel the PositionABS move.  Clear after travel time.
      zoomTargetCheckRef.current = setTimeout(() => {
        zoomTargetCheckRef.current = null;
        zoomTargetPct.current = null;
        loadPosition();
      }, 2000);
      return;
    }

    // Axis: continuous zoom + poll-based auto-stop
    zoomTargetPct.current = targetPct;
    const curPct = getZoomPct(positionRef.current.zoom) ?? 50;
    const dir = targetPct > curPct + 1.5 ? 1 : targetPct < curPct - 1.5 ? -1 : 0;
    if (dir === 0) return;

    handleZoomPress(dir); // starts move + position polling (zoomPollRef interval)

    // Check every 200ms; auto-stop when within 2% of target even after mouse release.
    zoomTargetCheckRef.current = setInterval(() => {
      const cur = getZoomPct(positionRef.current.zoom) ?? 50;
      const tgt = zoomTargetPct.current;
      if (tgt == null || Math.abs(cur - tgt) <= 2) {
        clearInterval(zoomTargetCheckRef.current);
        zoomTargetCheckRef.current = null;
        zoomTargetPct.current = null;
        handleZoomRelease();
      }
    }, 200);
  }

  function getSliderTargetPct(e) {
    const rect = zoomSliderTrackRef.current.getBoundingClientRect();
    return Math.max(0, Math.min(100, (1 - (e.clientY - rect.top) / rect.height) * 100));
  }

  function onZoomTrackPointerDown(e) {
    if (ptzDisabled) return;
    e.preventDefault();
    zoomPointerDown.current = true;
    e.currentTarget.setPointerCapture(e.pointerId);
    startZoomToTarget(getSliderTargetPct(e));
  }

  function onZoomTrackPointerMove(e) {
    if (!zoomPointerDown.current) return;
    startZoomToTarget(getSliderTargetPct(e));
  }

  function onZoomTrackPointerUp() {
    if (!zoomPointerDown.current) return;
    zoomPointerDown.current = false;
    // If target tracking is active (range-known mode), let the interval auto-stop the
    // camera when it reaches the target — don't interrupt it on mouse release.
    // If no tracking (range-unknown / direction mode), stop immediately on release.
    if (zoomTargetCheckRef.current == null) {
      handleZoomRelease();
    }
  }

  // Dahua: fire one move request; on completion, re-fire if desired direction changed.
  // This prevents request queue buildup when digest auth latency (~200ms first call,
  // ~50ms cached) is longer than the user's joystick polling rate.
  const sendSerialisedMove = useCallback((scaledPan, scaledTilt, speed) => {
    if (pendingStop.current) return;
    lastSentDir.current = { pan: scaledPan, tilt: scaledTilt };
    lastMoveSent.current = Date.now();
    moveInFlight.current = true;
    // Holding a steady direction sends no further moves (the chain only re-fires on a
    // direction change), so without this the server watchdog would cut a long hold short.
    startHoldKeepAlive(() => {
      const d = lastSentDir.current;
      if (d.pan === 0 && d.tilt === 0) return;
      ptzApi.ptzMove(camera.id, { pan: d.pan, tilt: d.tilt, continuousMove: true, speed }).catch(() => {});
    });
    ptzApi.ptzMove(camera.id, { pan: scaledPan, tilt: scaledTilt, continuousMove: true, speed })
      .then(() => {
        moveInFlight.current = false;
        if (!pendingStop.current) {
          const d = desiredDir.current;
          if (d.pan !== lastSentDir.current.pan || d.tilt !== lastSentDir.current.tilt) {
            sendSerialisedMove(d.pan, d.tilt, d.speed);
          }
        }
      })
      .catch(() => { moveInFlight.current = false; });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [camera.id]);

  const sendContinuousMove = useCallback((pan, tilt) => {
    if (pendingStop.current) return;
    const now = Date.now();

    // Hold the joystick's first moves back until the press is known to be a hold, keeping
    // the latest direction so the tap step (or the committed hold) uses where it ended up.
    if (driverRef.current?.continuousMove.tapAsStep && tapRef.current?.timer) {
      tapRef.current.pan = pan;
      tapRef.current.tilt = tilt;
      return;
    }

    if (driverRef.current?.continuousMove.serialise) {
      const s = joystickSpeedRef.current / 100;
      const scaledPan  = Math.round(pan  * s);
      const scaledTilt = Math.round(tilt * s);
      // Always update desired direction — in-flight completion picks it up
      desiredDir.current = { pan: scaledPan, tilt: scaledTilt, speed: joystickSpeedRef.current };
      // If a request is in-flight it will chain to the new desired direction on completion
      if (moveInFlight.current) return;
      // Already moving in this direction?
      if (scaledPan === lastSentDir.current.pan && scaledTilt === lastSentDir.current.tilt) return;
      sendSerialisedMove(scaledPan, scaledTilt, joystickSpeedRef.current);
      return;
    }

    // Axis: throttle-based continuous move
    if (now - lastMoveSent.current < 80) return;
    lastMoveSent.current = now;
    const s = joystickSpeedRef.current / 100;
    ptzApi.ptzMove(camera.id, {
      pan: Math.round(pan * s), tilt: Math.round(tilt * s),
      continuousMove: true, speed: joystickSpeedRef.current,
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [camera.id, sendSerialisedMove]);

  const handleZoomPress = useCallback((direction) => {
    onMove?.();
    zoomSpeedRef.current = readSpeed('zoom', '50'); // pick up live Settings-tab changes
    pendingStop.current = false;
    lastMoveSent.current = Date.now();
    // Some protocols carry the rate in the zoom value's magnitude (-100..100); others take a
    // bare direction and read the rate from the speed parameter.
    const zoomVal = driverRef.current?.zoomControl.moveValue === 'direction'
      ? direction
      : direction * zoomSpeedRef.current;
    const send = () => ptzApi.ptzMove(camera.id, { zoom: zoomVal, continuousMove: true, speed: zoomSpeedRef.current });
    send();
    startHoldKeepAlive(send); // held zoom sends nothing further on its own
    // Start polling position so the zoom slider updates in real time
    if (!zoomPollRef.current) {
      zoomPollRef.current = setInterval(loadPosition, 400);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [camera.id]);

  const stopMove = useCallback(async () => {
    pendingStop.current = true;
    stopHoldKeepAlive();
    lastSentDir.current = { pan: 0, tilt: 0 };
    desiredDir.current = { pan: 0, tilt: 0, speed: 50 }; // prevent chain re-fire after stop

    // The 250ms Dahua delay that used to sit here is gone. It was trying to guarantee the
    // stop reached the camera after the move, but it measured from when the move was SENT,
    // not when it arrived — and delivery was later measured varying 204-881ms, so 250ms
    // never actually guaranteed anything. It only added travel to every release. Ordering
    // is now enforced server-side, where it can be (routes/dahua.js withCameraLock), with
    // the hold watchdog as the backstop if a stop is lost entirely.
    try { await ptzApi.ptzStop(camera.id); } catch {}
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [camera.id]);

  const handleZoomRelease = useCallback(() => {
    clearInterval(zoomPollRef.current);
    zoomPollRef.current = null;
    stopMove();
    setTimeout(loadPosition, 350);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stopMove]);

  // Keep a Dahua hold alive against the backend watchdog by re-sending the active command.
  // Only continuous moves need this; taps are one-shot and have nothing to keep alive.
  function startHoldKeepAlive(resend) {
    if (!driverRef.current?.continuousMove.keepAliveMs) return;
    stopHoldKeepAlive();
    holdKeepAliveRef.current = setInterval(() => {
      if (pendingStop.current) { stopHoldKeepAlive(); return; }
      try { resend(); } catch (_) {}
    }, HOLD_KEEPALIVE_MS);
  }
  function stopHoldKeepAlive() {
    if (holdKeepAliveRef.current) {
      clearInterval(holdKeepAliveRef.current);
      holdKeepAliveRef.current = null;
    }
  }

  // --- Dahua tap/hold ---------------------------------------------------------------
  // A press released inside TAP_WINDOW_MS becomes ONE bounded recentre instead of a
  // continuous move. Continuous move cannot express a small step at these latencies: the
  // lens can't stop before the start command has reached it, so any tap travels at least
  // one one-way transit (200-450ms of motion, measured against a live camera) however
  // briefly the control is held. A tap is also where start and stop are most likely to be
  // delivered out of order, which leaves the camera moving with no stop to follow.
  // Holds keep using continuous move — that's the right primitive for steering, and its
  // overshoot on release is inherent to the protocol rather than a bug.

  // One bounded pan/tilt step, via ptzBase.cgi moveDirectly (the call click-to-center
  // already uses). Deliberately NOT PositionABS: that takes all three axes at once, and
  // on this firmware commanding even the camera's own current zoom still moves it +0.4x,
  // so pan/tilt steps routed through it would creep the zoom in. Working in frame
  // coordinates also makes a step scale with field of view — the same tap covers a wide
  // angle when zoomed out and a narrow one at full tele, which is what an operator expects.
  function sendTapStep(pan, tilt, speed) {
    const frac = TAP_MAX_FRAC * (Math.max(1, Math.min(100, speed)) / 100);
    const clamp01 = v => Math.max(0, Math.min(1, v));
    ptzApi.ptzCenter(camera.id, {
      x: clamp01(0.5 + (pan / 100) * frac),
      y: clamp01(0.5 - (tilt / 100) * frac), // frame y is top-down; +tilt is up
    }).catch(() => {});
    setTimeout(loadPosition, 800);
  }

  // Defer the continuous start so a quick press can still be sent as a single step.
  // Returns true when the press is being held pending that decision (Dahua only).
  function beginTapGesture(pan, tilt, speed, startHold) {
    if (!driverRef.current?.continuousMove.tapAsStep) return false;
    if (tapRef.current?.timer) clearTimeout(tapRef.current.timer);
    tapRef.current = {
      pan, tilt, speed,
      timer: setTimeout(() => {
        if (tapRef.current) tapRef.current.timer = null; // commit: this is a hold
        startHold();
      }, TAP_WINDOW_MS),
    };
    return true;
  }

  // Returns true if the release was consumed as a tap — nothing was started, so the
  // caller must not send a stop.
  function endTapGesture() {
    const t = tapRef.current;
    if (!t) return false;
    tapRef.current = null;
    if (!t.timer) return false; // hold already committed; caller stops it normally
    clearTimeout(t.timer);
    sendTapStep(t.pan, t.tilt, t.speed);
    return true;
  }

  function handleArrowPress(pan, tilt) {
    onMove?.();
    dpadSpeedRef.current = readSpeed('dpad', '50'); // pick up live Settings-tab changes
    pendingStop.current = false;
    const startHold = () => {
      lastMoveSent.current = Date.now();
      const s = dpadSpeedRef.current / 100;
      const send = () => ptzApi.ptzMove(camera.id, { pan: Math.round(pan * s), tilt: Math.round(tilt * s), continuousMove: true, speed: dpadSpeedRef.current });
      send();
      startHoldKeepAlive(send);
    };
    if (beginTapGesture(pan, tilt, dpadSpeedRef.current, startHold)) return;
    startHold();
  }
  function handleArrowRelease() {
    if (endTapGesture()) return; // was a tap — one step already sent, nothing to stop
    stopMove();
    setTimeout(loadPosition, 600);
  }

  const onJoystickMouseDown = useCallback((e) => {
    onMove?.();
    joystickSpeedRef.current = readSpeed('joy', '50'); // pick up live Settings-tab changes
    pendingStop.current = false;
    lastSentDir.current = { pan: 0, tilt: 0 };
    desiredDir.current = { pan: 0, tilt: 0, speed: 50 };
    moveInFlight.current = false; // discard previous gesture's in-flight tracking
    lastMoveSent.current = 0; // reset throttle so first move fires immediately
    isDragging.current = true;
    // Bound to the joystick base, so currentTarget IS the circle — no parentElement hop.
    const rect = e.currentTarget.getBoundingClientRect();
    joystickCenter.current = { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 };
    // Direction isn't known yet — the caller applies the press position immediately, and
    // pointermove keeps it current. A press with no deflection taps with (0,0), which
    // recentres on the centre and so moves nothing.
    beginTapGesture(0, 0, joystickSpeedRef.current, () => {
      const t = tapRef.current;
      if (t) sendContinuousMove(t.pan, t.tilt);
    });
    e.preventDefault();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onJoystickMove = useCallback((clientX, clientY) => {
    if (!isDragging.current || pendingStop.current) return;
    const dx = clientX - joystickCenter.current.x;
    const dy = clientY - joystickCenter.current.y;
    const maxRadius = JOY_MAX_R;
    const dist = Math.min(Math.sqrt(dx * dx + dy * dy), maxRadius);
    const angle = Math.atan2(dy, dx);
    const clampedX = dist * Math.cos(angle);
    const clampedY = dist * Math.sin(angle);
    if (joystickRef.current) {
      joystickRef.current.style.transform = `translate(calc(-50% + ${clampedX}px), calc(-50% + ${clampedY}px))`;
    }
    sendContinuousMove(Math.round((clampedX / maxRadius) * 100), Math.round((-clampedY / maxRadius) * 100));
  }, [sendContinuousMove]);

  const onJoystickRelease = useCallback(() => {
    if (!isDragging.current) return;
    isDragging.current = false;
    if (joystickRef.current) joystickRef.current.style.transform = 'translate(-50%, -50%)';
    if (endTapGesture()) return; // was a tap — one step already sent, nothing to stop
    stopMove();
    setTimeout(loadPosition, 600);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stopMove]);

  const onMouseMove = useCallback((e) => onJoystickMove(e.clientX, e.clientY), [onJoystickMove]);
  const onTouchMove = useCallback((e) => {
    const t = e.touches[0];
    if (t) onJoystickMove(t.clientX, t.clientY);
  }, [onJoystickMove]);

  useEffect(() => {
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onJoystickRelease);
    window.addEventListener('touchmove', onTouchMove, { passive: true });
    window.addEventListener('touchend', onJoystickRelease);
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onJoystickRelease);
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('touchend', onJoystickRelease);
      clearInterval(zoomTargetCheckRef.current);
      if (tapRef.current?.timer) clearTimeout(tapRef.current.timer);
      if (holdKeepAliveRef.current) clearInterval(holdKeepAliveRef.current);
    };
  }, [onMouseMove, onJoystickRelease, onTouchMove]);

  async function gotoHome() {
    try {
      await ptzApi.gotoHome(camera.id);
      addToast({ type: 'info', message: 'Moving to Home' });
      setTimeout(loadPosition, 2000);
    } catch {
      addToast({ type: 'error', message: 'Failed to go to Home position' });
    }
  }

  async function handleSnapshot() {
    setCapturing(true);
    try {
      const res = await snapshotApi.capture(camera.id);
      const disposition = res.headers['content-disposition'] || '';
      const nameMatch = disposition.match(/filename="?([^";\n]+)"?/);
      const filename = nameMatch ? nameMatch[1] : `snapshot_${camera.name}.jpg`;
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = filename; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      addToast({ type: 'success', message: 'Snapshot captured & downloaded' });
    } catch (err) {
      addToast({ type: 'error', message: err.response?.data?.error || 'Snapshot failed' });
    } finally { setCapturing(false); }
  }

  const btnStyle = {
    width: '44px', height: '44px',
    background: 'var(--bg-elevated)',
    border: '1px solid var(--border-bright)',
    borderRadius: 'var(--radius-md)',
    color: 'var(--text-secondary)',
    cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: '16px', transition: 'all 0.1s',
    userSelect: 'none', WebkitUserSelect: 'none',
  };

  // The d-pad keys fill their grid cell instead of sitting at 44px inside it. Sharing
  // btnStyle left a 32px dead strip after every key, which read as small buttons spaced far
  // apart, and pushed the visible cluster off-centre from the joystick above it. The glyph
  // scales with the key or a 76px button ends up with a 16px arrow lost in the middle.
  const dpadBtnStyle = { ...btnStyle, width: '100%', height: '100%', fontSize: '20px' };

  const ptzDisabled = activeTourId != null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>

      {/* Tour active banner */}
      {ptzDisabled && (
        <div style={{
          padding: '8px 14px',
          background: 'rgba(244,123,32,0.1)',
          border: '1px solid var(--accent-orange-dim)',
          borderRadius: 'var(--radius-md)',
          fontFamily: 'var(--font-mono)', fontSize: '11px',
          color: 'var(--accent-orange)',
          display: 'flex', alignItems: 'center', gap: '8px',
        }}>
          <div style={{ width: '7px', height: '7px', borderRadius: '50%', background: 'var(--accent-orange)', boxShadow: '0 0 6px var(--accent-orange)', flexShrink: 0 }} />
          Guard tour active — PTZ controls disabled.
        </div>
      )}

      {/* Controls: tall Zoom (left) + Joystick/D-Pad + Snapshot/Tours (right) */}
      <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start' }}>

        {/* Tall Zoom column — spans the full height, left of the joystick */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', opacity: ptzDisabled ? 0.35 : 1, pointerEvents: ptzDisabled ? 'none' : 'auto', transition: 'opacity 0.2s' }}>
          <div className="section-title" style={{ marginBottom: '2px' }}>Zoom</div>
          <button style={btnStyle} onMouseDown={() => handleZoomPress(1)} onMouseUp={handleZoomRelease} onMouseLeave={handleZoomRelease}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
              <line x1="11" y1="8" x2="11" y2="14"/><line x1="8" y1="11" x2="14" y2="11"/>
            </svg>
          </button>
          {(() => {
            const zPct = getZoomPct(position.zoom) ?? 0;
            return (
              <>
                <div
                  ref={zoomSliderTrackRef}
                  onPointerDown={onZoomTrackPointerDown}
                  onPointerMove={onZoomTrackPointerMove}
                  onPointerUp={onZoomTrackPointerUp}
                  onPointerCancel={onZoomTrackPointerUp}
                  style={{
                    width: '26px', height: '380px',
                    background: 'var(--bg-void)',
                    borderRadius: '13px',
                    border: '1px solid var(--border-bright)',
                    position: 'relative',
                    // A value track, not a resize handle — nothing here gets resized.
                    cursor: zoomPointerDown.current ? 'grabbing' : 'pointer',
                    touchAction: 'none',
                    userSelect: 'none',
                  }}
                >
                  {/* Colored fill — bottom to current zoom level */}
                  <div style={{
                    position: 'absolute', bottom: 0, left: 0, right: 0,
                    height: `${zPct}%`,
                    background: 'linear-gradient(to top, var(--accent-orange), rgba(244,123,32,0.4))',
                    borderRadius: '0 0 13px 13px',
                    transition: 'height 0.15s',
                    pointerEvents: 'none',
                  }} />
                  {/* Thumb — grabbable horizontal bar at current zoom level */}
                  <div style={{
                    position: 'absolute', left: '-3px', right: '-3px',
                    bottom: `calc(${zPct}% - 5px)`,
                    height: '10px',
                    background: 'var(--accent-orange)',
                    borderRadius: '5px',
                    boxShadow: '0 0 8px var(--accent-orange)',
                    transition: 'bottom 0.15s',
                    pointerEvents: 'none',
                  }} />
                </div>
                {/* Zoom percentage readout */}
                <div style={{
                  fontFamily: 'var(--font-mono)', fontSize: '10px',
                  color: zoomRangeKnown() ? 'var(--accent-orange)' : 'var(--text-muted)',
                  textAlign: 'center',
                  letterSpacing: '0.05em',
                }}>
                  {zoomRangeKnown() ? `${Math.round(zPct)}%` : '—'}
                </div>
              </>
            );
          })()}
          <button style={btnStyle} onMouseDown={() => handleZoomPress(-1)} onMouseUp={handleZoomRelease} onMouseLeave={handleZoomRelease}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
              <line x1="8" y1="11" x2="14" y2="11"/>
            </svg>
          </button>
        </div>

        {/* Right stack: joystick + d-pad, then snapshot + tours */}
        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '14px' }}>

      {/* Joystick above D-Pad. Side by side they had to share the column's width, which
          capped both near 130px while the space below them sat empty. Stacked, each gets the
          full width, so the circle and every d-pad key become far bigger targets — this is
          the panel operators actually drive the camera from. */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '18px', alignItems: 'flex-start', opacity: ptzDisabled ? 0.35 : 1, pointerEvents: ptzDisabled ? 'none' : 'auto', transition: 'opacity 0.2s' }}>
        {/* Joystick */}
        <div>
          <div className="section-title" style={{ marginBottom: '10px' }}>Joystick</div>
          {/* Press anywhere on the base, not just the 36px knob: the whole circle is the
              control surface. An off-centre press is itself a deflection, so it applies
              immediately rather than waiting for a drag, and the knob jumps to meet the
              pointer. Mouse and touch share one path so both get tap/hold handling. */}
          <div
            onMouseDown={(e) => { onJoystickMouseDown(e); onJoystickMove(e.clientX, e.clientY); }}
            onTouchStart={(e) => {
              const t = e.touches[0];
              if (!t) return;
              onJoystickMouseDown(e);
              onJoystickMove(t.clientX, t.clientY);
            }}
            style={{
              width: `${JOY_BASE}px`, height: `${JOY_BASE}px`,
              background: 'var(--bg-surface)', borderRadius: '50%',
              border: '2px solid var(--border-bright)', position: 'relative',
              cursor: 'crosshair', boxShadow: 'inset 0 0 20px rgba(0,0,0,0.5)',
              touchAction: 'none',
            }}>
            <div style={{ position: 'absolute', left: '50%', top: 0, bottom: 0, width: '1px', background: 'var(--border-dim)', transform: 'translateX(-50%)' }} />
            <div style={{ position: 'absolute', top: '50%', left: 0, right: 0, height: '1px', background: 'var(--border-dim)', transform: 'translateY(-50%)' }} />
            <div
              ref={joystickRef}
              style={{
                position: 'absolute', left: '50%', top: '50%',
                transform: 'translate(-50%, -50%)',
                width: `${JOY_KNOB}px`, height: `${JOY_KNOB}px`,
                background: 'radial-gradient(circle at 40% 35%, var(--accent-orange), var(--accent-orange-dim))',
                borderRadius: '50%', cursor: 'grab',
                boxShadow: '0 0 12px rgba(244,123,32,0.4)', zIndex: 2,
                pointerEvents: 'none', // the base owns all pointer input
              }}
            />
          </div>
        </div>

        {/* D-Pad. Width is pinned to the joystick's and the grid centred inside it, so the
            two stay centred on each other if either size changes later. They happen to be
            equal right now, which would make the alignment look deliberate while actually
            being a coincidence. */}
        <div style={{ width: `${JOY_BASE}px` }}>
          <div className="section-title" style={{ marginBottom: '10px' }}>D-Pad</div>
          <div style={{ display: 'grid', justifyContent: 'center', gridTemplateColumns: `repeat(3, ${DPAD_CELL}px)`, gridTemplateRows: `repeat(3, ${DPAD_CELL}px)`, gap: `${DPAD_GAP}px` }}>
            <div />
            <button style={dpadBtnStyle} onMouseDown={() => handleArrowPress(0, 100)} onMouseUp={handleArrowRelease} onMouseLeave={handleArrowRelease}>▲</button>
            <div />
            <button style={dpadBtnStyle} onMouseDown={() => handleArrowPress(-100, 0)} onMouseUp={handleArrowRelease} onMouseLeave={handleArrowRelease}>◄</button>
            <button style={{ ...dpadBtnStyle, background: 'rgba(244,123,32,0.1)', color: 'var(--accent-orange)', border: '1px solid var(--accent-orange-dim)' }} onClick={gotoHome} title="Go to Home">⌂</button>
            <button style={dpadBtnStyle} onMouseDown={() => handleArrowPress(100, 0)} onMouseUp={handleArrowRelease} onMouseLeave={handleArrowRelease}>►</button>
            <div />
            <button style={dpadBtnStyle} onMouseDown={() => handleArrowPress(0, -100)} onMouseUp={handleArrowRelease} onMouseLeave={handleArrowRelease}>▼</button>
            <div />
          </div>
        </div>
      </div>

          {/* Snapshot + Guard Tours */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', minWidth: 0 }}>

          {/* Snapshot */}
          <div>
            <div className="section-title" style={{ marginBottom: '8px' }}>Snapshot</div>
            <button
              className="btn btn-ghost btn-sm"
              style={{ width: '100%', justifyContent: 'center' }}
              onClick={handleSnapshot}
              disabled={capturing || !camera.status?.online}
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2z"/><circle cx="12" cy="13" r="4"/></svg>
              {capturing ? 'Capturing...' : 'Capture & Download'}
            </button>
          </div>

          {/* Patrol Tours */}
          <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' }}>
              <div className="section-title">Patrol Tours</div>
              <button className="btn btn-ghost btn-sm" onClick={loadTours} disabled={toursLoading} style={{ padding: '2px 6px' }}>
                {toursLoading ? '...' : (
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 102.13-9.36L1 10"/></svg>
                )}
              </button>
            </div>
            {!toursLoading && tours.length === 0 ? (
              <div style={{ fontFamily: 'var(--font-mono)', fontSize: '10px', color: 'var(--text-muted)', textAlign: 'center', padding: '8px', opacity: 0.6 }}>
                No guard tours found
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                {tours.map(tour => {
                  const isActive = activeTourId === tour.id;
                  return (
                    <div key={tour.id} style={{
                      display: 'flex', alignItems: 'center', gap: '8px',
                      padding: '6px 10px',
                      background: isActive ? 'rgba(244,123,32,0.08)' : 'var(--bg-surface)',
                      border: `1px solid ${isActive ? 'var(--accent-orange)' : 'var(--border-dim)'}`,
                      borderRadius: 'var(--radius-md)', transition: 'all 0.15s',
                    }}>
                      <div style={{ width: '6px', height: '6px', borderRadius: '50%', flexShrink: 0, background: isActive ? 'var(--accent-orange)' : 'var(--border-bright)', boxShadow: isActive ? '0 0 6px var(--accent-orange)' : 'none', transition: 'all 0.3s' }} />
                      <div style={{ flex: 1, minWidth: 0, fontFamily: 'var(--font-display)', fontSize: '11px', fontWeight: 600, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {tour.name}
                      </div>
                      <button
                        className={`btn btn-sm ${isActive ? 'btn-primary' : 'btn-ghost'}`}
                        style={{ padding: '3px 9px', fontSize: '10px', flexShrink: 0 }}
                        onClick={() => toggleTour(tour)}
                        disabled={tourActionId === tour.id}
                      >
                        {tourActionId === tour.id ? '...' : isActive ? 'Stop' : 'Start'}
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          </div>
        </div>
      </div>

    </div>
  );
}
